import { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { currencySymbol, formatCurrency } from '@/utils/formatCurrency';
import { adsApi } from '@/api/client';
import {
  parseAsinList,
  parseCategoryRecommendationsPayload,
  parseProductMetadataPayload,
  parseProductRecommendationsPayload,
  productTargetRowKey,
} from '@/utils/spCampaignCreateDraft';
import type {
  SpAddedProductTarget,
  SpAdvertisedProduct,
  SpCategorySuggestion,
  SpProductTargetMatch,
} from '@/types/spCampaignCreate';
import { Spinner } from '@/components/ui/Spinner';

type PrimaryTab = 'categories' | 'products';
type CategorySubTab = 'suggested' | 'search';
type ProductSubTab = 'suggested' | 'search' | 'list';

const BID_FORMAT = { minimumFractionDigits: 2, maximumFractionDigits: 2 } as const;

function money(value: number): string {
  return formatCurrency(value, undefined, BID_FORMAT);
}

function bidRangeLabel(cat: { bidMin: number | null; bidMax: number | null }): string | null {
  if (cat.bidMin == null || cat.bidMax == null) return null;
  return `${money(cat.bidMin)} – ${money(cat.bidMax)}`;
}

function defaultBidFor(defaultBid: string, suggested: number | null): string {
  if (suggested != null) return String(suggested);
  return defaultBid;
}

export function ProductTargetingPanel({
  profileId,
  advertisedProducts,
  defaultBid,
  added,
  onChange,
  disabled,
  adProduct = 'SP',
  campaignId,
}: {
  profileId: string;
  advertisedProducts: SpAdvertisedProduct[];
  defaultBid: string;
  added: SpAddedProductTarget[];
  onChange: (rows: SpAddedProductTarget[]) => void;
  disabled?: boolean;
  adProduct?: 'SP' | 'SB';
  campaignId?: string;
}) {
  const [primary, setPrimary] = useState<PrimaryTab>('categories');
  const [categoryTab, setCategoryTab] = useState<CategorySubTab>('suggested');
  const [productTab, setProductTab] = useState<ProductSubTab>('suggested');
  const [matchFilters, setMatchFilters] = useState<SpProductTargetMatch[]>(['EXPANDED']);
  const [categoryQuery, setCategoryQuery] = useState('');
  const [productQuery, setProductQuery] = useState('');
  const [listText, setListText] = useState('');
  const [categories, setCategories] = useState<SpCategorySuggestion[]>([]);
  const [suggestedAsins, setSuggestedAsins] = useState<Array<{ asin: string; title?: string; imageUrl?: string }>>([]);
  const [searchProducts, setSearchProducts] = useState<Array<{ asin: string; title?: string; imageUrl?: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const asins = useMemo(
    () => advertisedProducts.map((p) => p.asin).filter(Boolean),
    [advertisedProducts]
  );
  const addedKeys = useMemo(
    () => new Set(added.map((row) => productTargetRowKey(row.expressionType, row.value))),
    [added]
  );

  useEffect(() => {
    if (asins.length === 0) {
      setCategories([]);
      setSuggestedAsins([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    const categoryRequest =
      adProduct === 'SB'
        ? adsApi.getSbCategoryRecommendations(profileId, {
            asins,
            ...(campaignId ? { campaignId: campaignId.toString() } : {}),
          })
        : adsApi.getSpCategoryRecommendations(profileId, { asins, includeAncestor: true });
    const productRequest =
      adProduct === 'SB'
        ? adsApi.getSbProductRecommendations(profileId, { asins, maxResults: 20 })
        : adsApi.getSpProductRecommendations(profileId, { adAsins: asins, count: 20 });
    void Promise.all([categoryRequest, productRequest])
      .then(async ([catRes, prodRes]) => {
        if (cancelled) return;
        const nextCategories = catRes.success ? parseCategoryRecommendationsPayload(catRes.data) : [];
        setCategories(nextCategories);
        const recAsins = prodRes.success ? parseProductRecommendationsPayload(prodRes.data) : [];
        if (recAsins.length === 0) {
          setSuggestedAsins([]);
          return;
        }
        const meta = await adsApi.getProductMetadata(profileId, {
          adType: adProduct,
          asins: recAsins,
          pageSize: Math.min(100, Math.max(25, recAsins.length)),
          pageIndex: 0,
        });
        const hydrated = meta.success ? parseProductMetadataPayload(meta.data) : [];
        const byAsin = new Map(hydrated.map((p) => [p.asin.toUpperCase(), p]));
        setSuggestedAsins(
          recAsins.map((asin) => ({
            asin,
            title: byAsin.get(asin)?.title,
            imageUrl: byAsin.get(asin)?.imageUrl,
          }))
        );
      })
      .catch(() => {
        if (!cancelled) setError('Could not load targeting suggestions.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [adProduct, asins, campaignId, profileId]);

  const filteredCategories = categories.filter((cat) => {
    if (!categoryQuery.trim()) return true;
    const q = categoryQuery.toLowerCase();
    return cat.name.toLowerCase().includes(q) || cat.path.toLowerCase().includes(q);
  });

  const addTarget = (row: Omit<SpAddedProductTarget, 'bid' | 'suggestedBid'> & { suggestedBid?: number | null }) => {
    const key = productTargetRowKey(row.expressionType, row.value);
    if (addedKeys.has(key)) return;
    onChange([
      ...added,
      {
        ...row,
        suggestedBid: row.suggestedBid ?? null,
        bid: defaultBidFor(defaultBid, row.suggestedBid ?? null),
      },
    ]);
  };

  const expressionTypes = (): Array<'ASIN_SAME_AS' | 'ASIN_EXPANDED_FROM'> => {
    const types: Array<'ASIN_SAME_AS' | 'ASIN_EXPANDED_FROM'> = [];
    if (matchFilters.includes('EXACT')) types.push('ASIN_SAME_AS');
    if (matchFilters.includes('EXPANDED')) types.push('ASIN_EXPANDED_FROM');
    return types.length ? types : ['ASIN_EXPANDED_FROM'];
  };

  const searchCatalog = async () => {
    if (!productQuery.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const res = await adsApi.getProductMetadata(profileId, {
        adType: adProduct,
        searchStr: productQuery.trim(),
        pageSize: 25,
      });
      if (!res.success) {
        setError(res.message || 'No products found.');
        setSearchProducts([]);
        return;
      }
      setSearchProducts(
        parseProductMetadataPayload(res.data).map((p) => ({
          asin: p.asin,
          title: p.title,
          imageUrl: p.imageUrl,
        }))
      );
    } catch {
      setError('No products found.');
      setSearchProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const addProductAsins = (items: Array<{ asin: string; title?: string; imageUrl?: string }>) => {
    const types = expressionTypes();
    const next = [...added];
    const keys = new Set(addedKeys);
    for (const item of items) {
      for (const expressionType of types) {
        const key = productTargetRowKey(expressionType, item.asin);
        if (keys.has(key)) continue;
        keys.add(key);
        next.push({
          id: key,
          kind: 'PRODUCT',
          expressionType,
          value: item.asin,
          label: item.title || item.asin,
          imageUrl: item.imageUrl,
          suggestedBid: null,
          bid: defaultBidFor(defaultBid, null),
        });
      }
    }
    onChange(next);
  };

  const addProductAsin = (asin: string, title?: string, imageUrl?: string) => {
    addProductAsins([{ asin, title, imageUrl }]);
  };

  return (
    <fieldset className="border border-border rounded-xl p-4 space-y-3">
      <legend className="text-sm font-medium text-foreground-muted px-1">Product targeting</legend>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="space-y-3">
          <div className="flex gap-2 text-sm">
            {(['categories', 'products'] as const).map((id) => (
              <button
                key={id}
                type="button"
                className={`px-3 py-1 rounded-lg border ${
                  primary === id ? 'border-brand text-brand bg-brand/5' : 'border-border'
                }`}
                onClick={() => setPrimary(id)}
                disabled={disabled}
              >
                {id === 'categories' ? 'Categories' : 'Individual products'}
              </button>
            ))}
          </div>

          {primary === 'categories' ? (
            <>
              <div className="flex gap-2 text-sm">
                {(['suggested', 'search'] as const).map((id) => (
                  <button
                    key={id}
                    type="button"
                    className={`px-3 py-1 rounded-lg border ${
                      categoryTab === id ? 'border-brand text-brand' : 'border-border'
                    }`}
                    onClick={() => setCategoryTab(id)}
                    disabled={disabled}
                  >
                    {id === 'suggested' ? 'Suggested' : 'Search'}
                  </button>
                ))}
              </div>
              {categoryTab === 'search' && (
                <input
                  type="text"
                  value={categoryQuery}
                  onChange={(e) => setCategoryQuery(e.target.value)}
                  placeholder="Filter suggested categories"
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm"
                  disabled={disabled}
                />
              )}
              {loading && categories.length === 0 ? (
                <div className="flex items-center gap-2 text-sm text-foreground-muted">
                  <Spinner className="w-4 h-4" /> Loading categories…
                </div>
              ) : filteredCategories.length === 0 ? (
                <p className="text-sm text-foreground-subtle">
                  {error || 'No relevant categories available. Try searching for categories.'}
                </p>
              ) : (
                <div className="border border-border rounded-lg overflow-hidden">
                  <div className="flex items-center gap-2 px-3 py-2 text-xs text-foreground-muted bg-surface-muted">
                    <span className="flex-1">{filteredCategories.length} suggestions</span>
                    <span className="w-28 text-right">Sugg. bid</span>
                  </div>
                  <ul className="divide-y divide-border max-h-72 overflow-auto">
                    {filteredCategories.map((cat) => {
                      const key = productTargetRowKey('ASIN_CATEGORY_SAME_AS', cat.id);
                      const already = addedKeys.has(key);
                      const range = bidRangeLabel(cat);
                      return (
                        <li key={cat.id} className="flex items-center gap-2 px-3 py-2 text-sm">
                          <div className="min-w-0 flex-1">
                            <p className="truncate">{cat.name}</p>
                            <p className="text-xs text-foreground-muted truncate">{cat.path}</p>
                            {cat.productCountLabel && (
                              <p className="text-xs text-foreground-muted">Products: {cat.productCountLabel}</p>
                            )}
                          </div>
                          <div className="w-28 text-right">
                            <p>{cat.suggestedBid != null ? money(cat.suggestedBid) : '—'}</p>
                            {range && <p className="text-xs text-foreground-muted">{range}</p>}
                          </div>
                          <button
                            type="button"
                            className="text-brand disabled:opacity-40"
                            disabled={disabled || already || !cat.canBeTargeted}
                            onClick={() =>
                              addTarget({
                                id: key,
                                kind: 'CATEGORY',
                                expressionType: 'ASIN_CATEGORY_SAME_AS',
                                value: cat.id,
                                label: cat.name,
                                path: cat.path,
                                suggestedBid: cat.suggestedBid,
                              })
                            }
                          >
                            {already ? 'Added' : 'Add'}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="flex flex-wrap gap-2 text-sm">
                {(['suggested', 'search', 'list'] as const).map((id) => (
                  <button
                    key={id}
                    type="button"
                    className={`px-3 py-1 rounded-lg border ${
                      productTab === id ? 'border-brand text-brand' : 'border-border'
                    }`}
                    onClick={() => setProductTab(id)}
                    disabled={disabled}
                  >
                    {id === 'suggested' ? 'Suggested' : id === 'search' ? 'Search' : 'Enter list'}
                  </button>
                ))}
              </div>
              <div className="flex gap-3 text-sm">
                {(['EXACT', 'EXPANDED'] as const).map((match) => (
                  <label key={match} className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={matchFilters.includes(match)}
                      onChange={() =>
                        setMatchFilters((prev) =>
                          prev.includes(match) ? prev.filter((m) => m !== match) : [...prev, match]
                        )
                      }
                      disabled={disabled}
                    />
                    {match.charAt(0) + match.slice(1).toLowerCase()}
                  </label>
                ))}
              </div>
              {productTab === 'search' && (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={productQuery}
                    onChange={(e) => setProductQuery(e.target.value)}
                    placeholder="Search products"
                    className="flex-1 border border-border rounded-lg px-3 py-2 text-sm"
                    disabled={disabled}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        void searchCatalog();
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="px-3 py-2 text-sm border border-border rounded-lg"
                    disabled={disabled || !productQuery.trim()}
                    onClick={() => void searchCatalog()}
                  >
                    Search
                  </button>
                </div>
              )}
              {productTab === 'list' && (
                <div className="space-y-2">
                  <textarea
                    value={listText}
                    onChange={(e) => setListText(e.target.value)}
                    placeholder="Enter ASINs, one per line"
                    className="w-full min-h-[88px] border border-border rounded-lg px-3 py-2 text-sm"
                    disabled={disabled}
                  />
                  <button
                    type="button"
                    className="text-sm text-brand"
                    disabled={disabled || !listText.trim()}
                    onClick={() => {
                      addProductAsins(parseAsinList(listText).map((asin) => ({ asin })));
                      setListText('');
                    }}
                  >
                    Add ASINs
                  </button>
                </div>
              )}
              {productTab !== 'list' && (
                loading && (productTab === 'suggested' ? suggestedAsins.length === 0 : searchProducts.length === 0) ? (
                  <div className="flex items-center gap-2 text-sm text-foreground-muted">
                    <Spinner className="w-4 h-4" /> Loading products…
                  </div>
                ) : (productTab === 'suggested' ? suggestedAsins : searchProducts).length === 0 ? (
                  <p className="text-sm text-foreground-subtle">
                    {error ||
                      (productTab === 'suggested'
                        ? 'No suggestions available. Try searching for products.'
                        : 'No products found.')}
                  </p>
                ) : (
                  <ul className="divide-y divide-border border border-border rounded-lg max-h-72 overflow-auto">
                    {(productTab === 'suggested' ? suggestedAsins : searchProducts).map((product) => (
                      <li key={product.asin} className="flex items-center gap-2 px-3 py-2 text-sm">
                        {product.imageUrl ? (
                          <img src={product.imageUrl} alt="" className="w-8 h-8 rounded object-cover" />
                        ) : (
                          <div className="w-8 h-8 rounded bg-surface-muted" />
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="truncate">{product.title || product.asin}</p>
                          <p className="text-xs text-foreground-muted">ASIN: {product.asin}</p>
                        </div>
                        <button
                          type="button"
                          className="text-brand"
                          disabled={disabled}
                          onClick={() => addProductAsin(product.asin, product.title, product.imageUrl)}
                        >
                          Add
                        </button>
                      </li>
                    ))}
                  </ul>
                )
              )}
            </>
          )}
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium">{added.length} added</p>
            {added.length > 0 && (
              <button type="button" className="text-sm text-brand" disabled={disabled} onClick={() => onChange([])}>
                Remove all
              </button>
            )}
          </div>
          {added.length === 0 ? (
            <p className="text-sm text-foreground-subtle border border-dashed border-border rounded-lg p-4">
              Added categories and products will appear here.
            </p>
          ) : (
            <ul className="divide-y divide-border border border-border rounded-lg max-h-96 overflow-auto">
              {added.map((row) => {
                const key = productTargetRowKey(row.expressionType, row.value);
                const bidNum = parseFloat(row.bid);
                const missingBid = Number.isNaN(bidNum) || bidNum <= 0;
                return (
                  <li key={key} className="p-2 space-y-1">
                    <div className="flex items-center gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm truncate">{row.path || row.label}</p>
                        <p className="text-xs text-foreground-muted">
                          {row.kind === 'CATEGORY' ? 'Category' : row.expressionType === 'ASIN_EXPANDED_FROM' ? 'Expanded' : 'Exact'}
                        </p>
                      </div>
                      <span className="text-xs text-foreground-muted w-16 text-right">
                        {row.suggestedBid != null ? formatCurrency(row.suggestedBid, undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}
                      </span>
                      <div className="flex items-center gap-1">
                        <span className="text-xs">{currencySymbol() ?? ''}</span>
                        <input
                          type="number"
                          min="0.01"
                          step="0.01"
                          value={row.bid}
                          onChange={(e) =>
                            onChange(added.map((item) => (item.id === row.id ? { ...item, bid: e.target.value } : item)))
                          }
                          className="w-20 border border-border rounded px-2 py-1 text-sm"
                          disabled={disabled}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => onChange(added.filter((item) => item.id !== row.id))}
                        disabled={disabled}
                        aria-label="Remove target"
                      >
                        <X className="w-4 h-4 text-foreground-muted" />
                      </button>
                    </div>
                    {missingBid && <p className="text-xs text-error">Enter a bid</p>}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </fieldset>
  );
}
